import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  TextField,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PeopleIcon from '@mui/icons-material/People';
import api from '../../utils/api';
import PageHeader from '../../components/Layout/PageHeader';
import { surfaceCardSx, tableHeadCellSx, tokens } from '../../theme/tokens';

export default function MembershipReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [useAcademicYear, setUseAcademicYear] = useState(false);

  useEffect(() => {
    fetchReport();
  }, [year, useAcademicYear]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/reports/membership', {
        params: { year, academicYear: useAcademicYear },
      });
      setData(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch membership report');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <PageHeader crumbs={<>Insight / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Reports</Box></>} title="Membership report" />
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress sx={{ color: tokens.primary }} />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <PageHeader crumbs={<>Insight / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Reports</Box></>} title="Membership report" />
        <Alert severity="error" sx={{ borderRadius: '9px', bgcolor: tokens.dangerSoft, color: '#7C2E1D', border: '1px solid #EFCFC5' }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const s = data?.summary || {};
  const yearLabel = data?.yearLabel || year;

  const summaryCards = [
    {
      title: 'Members completed school',
      value: s.membersCompletedSchool ?? 0,
      subtext: `${s.membersCompletedSchoolThisPeriod ?? 0} completed during this period`,
      icon: <SchoolIcon sx={{ fontSize: 22 }} />,
    },
    {
      title: 'Members recruited',
      value: s.membersRecruited ?? 0,
      subtext: `Joined during ${yearLabel}`,
      icon: <PersonAddIcon sx={{ fontSize: 22 }} />,
    },
    {
      title: 'Members still active',
      value: s.membersActive ?? 0,
      subtext: `Out of ${s.totalMembers ?? 0} total members`,
      icon: <CheckCircleIcon sx={{ fontSize: 22 }} />,
    },
    {
      title: 'Total members',
      value: s.totalMembers ?? 0,
      subtext: 'All time',
      icon: <PeopleIcon sx={{ fontSize: 22 }} />,
    },
  ];

  return (
    <Box>
      <PageHeader
        crumbs={<>Insight / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Reports</Box></>}
        title="Membership report"
        actions={
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              type="number"
              label="Year"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              size="small"
              sx={{ width: 120 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={useAcademicYear}
                  onChange={(e) => setUseAcademicYear(e.target.checked)}
                  color="primary"
                />
              }
              label="Academic year (Oct–Sept)"
            />
          </Box>
        }
      />
      <Typography sx={{ color: tokens.muted, fontSize: 13, mb: 2, mt: -1.5 }}>
        End-of-year summary for {yearLabel} (ATU calendar)
      </Typography>

      <Grid container spacing={2}>
        {summaryCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.title}>
            <Paper elevation={0} sx={{ ...surfaceCardSx, p: 2.5, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography sx={{ fontSize: 12, color: tokens.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.75 }}>
                    {card.title}
                  </Typography>
                  <Typography sx={{ fontFamily: tokens.fontDisplay, fontSize: 28, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {card.value.toLocaleString()}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: tokens.muted, mt: 0.5 }}>
                    {card.subtext}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '8px',
                    bgcolor: tokens.primarySoft,
                    color: tokens.primary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {card.icon}
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}

        <Grid item xs={12}>
          <Paper elevation={0} sx={{ ...surfaceCardSx, overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${tokens.border}` }}>
              <Typography sx={{ fontFamily: tokens.fontDisplay, fontWeight: 600, fontSize: 15 }}>
                Status breakdown
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeadCellSx}>Status</TableCell>
                    <TableCell sx={tableHeadCellSx} align="right">Count</TableCell>
                    <TableCell sx={tableHeadCellSx} align="right">Percentage</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.statusBreakdown || []).map((row) => (
                    <TableRow key={row.status} sx={{ '& td': { borderColor: tokens.border } }}>
                      <TableCell>{row.status || '(empty)'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{row.count}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {s.totalMembers > 0 ? ((row.count / s.totalMembers) * 100).toFixed(1) : 0}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
